import type Quill from 'quill/core/quill'
import merge from 'lodash/merge'
import Delta from 'quill-delta'
import { DeleteAction, ImageSpec, ResizeAction } from 'quill-blot-formatter'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback, useEffect, useState } from 'react'

import { type QuillEditorProps } from '..'
import { toast } from '@/components/toast'
import useFileArchiveUpload from '@/utils/hooks/useFileArchiveUpload'
import { useCompany } from '@/utils/hooks/useCompany'
import {
  endImageBlock,
  ImageAlignAction,
  startImageBlock,
} from './ImageAlignment'
import { pdfToHtml } from './import-pdf'
import { pastedImageFiles } from './pasted-image-files'

class CustomImageSpec extends ImageSpec {
  getActions() {
    return [ImageAlignAction, DeleteAction, ResizeAction]
  }
}

type Props = {
  isInitialized: boolean
  quillEditorRef: React.RefObject<Quill | null>
  setTablePickerVisible: React.Dispatch<React.SetStateAction<boolean>>
  isMobile: boolean
  placeholder: QuillEditorProps['placeholder']
}

type Return = NonNullable<QuillEditorProps['options']> | null

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

const useOptions = (props: Props): Return => {
  const {
    isInitialized,
    quillEditorRef,
    setTablePickerVisible,
    isMobile,
    placeholder,
  } = props

  const translations = useTranslate({
    alignLeft: 'quill.align-image-left',
    alignRight: 'quill.align-image-right',
    alignBreakText: 'quill.align-image-break-text',
    importPdf: 'quill.import-pdf',
    importPdfLoading: 'quill.import-pdf-loading',
    importPdfNoText: 'quill.import-pdf-no-text',
    importPdfFailed: 'quill.import-pdf-failed',
  })

  const { id: companyId } = useCompany()
  const [uploadImageMutation] = useFileArchiveUpload({
    onCompleted: () => {
      // Handle successful upload if needed
    },
  })

  const [options, setOptions] = useState<NonNullable<
    QuillEditorProps['options']
  > | null>(null)

  const uploadImageAndInsertAtSelection = useCallback(
    async (file: File) => {
      if (!quillEditorRef.current) return

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert('Unsupported image type!')
        return
      }

      const quill = quillEditorRef.current
      const range = quill.getSelection(true) ?? {
        index: 0,
        length: 0,
      }

      try {
        const response = await uploadImageMutation({
          variables: {
            input: {
              file: file,
              description: '',
              company: companyId,
              status: 'PENDING',
            },
          },
        })

        const id = response.data?.createFileArchive.fileArchive.id
        const imageUrl = response.data?.createFileArchive.fileArchive.url

        if (imageUrl) {
          const index = startImageBlock(quill, range.index)

          quill.insertEmbed(index, 'image', imageUrl)
          quill.formatText(index, 1, {
            width: '100%',
            height: 'auto',
            'data-id': id,
          })
          quill.setSelection(endImageBlock(quill, index))
        } else throw new Error('No image URL returned from upload')
      } catch (error) {
        console.error('Upload failed', error)
      }
    },
    [companyId, quillEditorRef, uploadImageMutation]
  )

  const importPdfAtSelection = useCallback(
    async (file: File) => {
      const quill = quillEditorRef.current
      if (!quill) return

      const range = quill.getSelection(true) ?? { index: 0, length: 0 }
      const toastId = toast.loading(translations.importPdfLoading)

      try {
        const html = await pdfToHtml(file)
        if (!html) {
          toast.error(translations.importPdfNoText, { id: toastId })
          return
        }

        const content = quill.clipboard.convert({ html })
        quill.updateContents(
          new Delta().retain(range.index).delete(range.length).concat(content),
          'user'
        )
        quill.setSelection(range.index + content.length(), 'silent')
        toast.dismiss(toastId)
      } catch (error) {
        console.error('PDF import failed', error)
        toast.error(translations.importPdfFailed, { id: toastId })
      }
    },
    [
      quillEditorRef,
      translations.importPdfFailed,
      translations.importPdfLoading,
      translations.importPdfNoText,
    ]
  )

  useEffect(() => {
    if (isInitialized) return // Make sure to only set options once

    const defaultOptions: NonNullable<QuillEditorProps['options']> = {
      theme: 'snow',
      placeholder,
      modules: {
        table: true,
        blotFormatter: {
          specs: [CustomImageSpec],
          align: {
            titles: {
              left: translations.alignLeft,
              right: translations.alignRight,
              breakText: translations.alignBreakText,
            },
            toolbar: {
              allowDeselect: false,
              buttonStyle: {
                boxSizing: 'border-box',
                cursor: 'pointer',
              },
              svgStyle: {
                display: 'block',
                width: '18px',
                height: '18px',
                margin: '2px',
                background: 'none',
                border: 'none',
              },
            },
          },
        },
        toolbar: {
          container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [
              { align: '' },
              { align: 'center' },
              { align: 'right' },
              { align: 'justify' },
            ],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            ['blockquote', 'link'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            ['image', 'video', 'pdf'],
            ['table'],
          ],
          handlers: {
            table: function (value: any) {
              if (value) {
                setTablePickerVisible(true)
              }
            },
            image: () => {
              // 1. Lag din egen input med 'accept'
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', ALLOWED_IMAGE_TYPES.join(','))
              input.click()

              input.onchange = async () => {
                if (!quillEditorRef.current || !input?.files) return

                const file = input.files?.[0]
                if (!file) return
                await uploadImageAndInsertAtSelection(file)
              }
            },
            pdf: () => {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'application/pdf')
              input.click()

              input.onchange = async () => {
                const file = input.files?.[0]
                if (file) await importPdfAtSelection(file)
              }
            },
          },
        },
      },
    }

    const mobileOptions: NonNullable<QuillEditorProps['options']> = {
      modules: {
        toolbar: {
          container: [
            { header: [1, 2, 3, 4, 5, 6, false] },
            { align: '' },
            { align: 'center' },
            { align: 'right' },
            { align: 'justify' },
            'bold',
            'italic',
            'underline',
            'strike',
            { color: [] },
            { background: [] },
            'blockquote',
            'link',
            { list: 'ordered' },
            { list: 'bullet' },
            'image',
            'video',
            'pdf',
            'table',
          ],
        },
      },
    }

    if (isMobile) setOptions(merge({}, defaultOptions, mobileOptions))
    else setOptions(defaultOptions)
  }, [
    importPdfAtSelection,
    isMobile,
    isInitialized,
    placeholder,
    quillEditorRef,
    setTablePickerVisible,
    translations.alignBreakText,
    translations.alignLeft,
    translations.alignRight,
    uploadImageAndInsertAtSelection,
  ])

  useEffect(() => {
    const quill = quillEditorRef.current
    if (!isInitialized || !quill) return

    const toolbar = quill.getModule('toolbar') as { container?: HTMLElement }
    toolbar.container
      ?.querySelector('.ql-pdf')
      ?.setAttribute('title', translations.importPdf)
  }, [isInitialized, quillEditorRef, translations.importPdf])

  useEffect(() => {
    const quill = quillEditorRef.current
    if (!isInitialized || !quill) return

    const handlePaste = (event: ClipboardEvent) => {
      const imageFiles = pastedImageFiles(event.clipboardData)
      if (!imageFiles.length) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      for (const file of imageFiles) {
        void uploadImageAndInsertAtSelection(file)
      }
    }

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? []).filter(file =>
        file.type.startsWith('image/')
      )

      if (!files.length) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      quill.focus()

      for (const file of files) {
        void uploadImageAndInsertAtSelection(file)
      }
    }

    quill.root.addEventListener('paste', handlePaste, true)
    quill.root.addEventListener('drop', handleDrop, true)

    return () => {
      quill.root.removeEventListener('paste', handlePaste, true)
      quill.root.removeEventListener('drop', handleDrop, true)
    }
  }, [isInitialized, quillEditorRef, uploadImageAndInsertAtSelection])

  return options
}

export default useOptions
